import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronDown,
  ChevronRight,
  Sparkles,
  Wrench,
  Bug,
  ImageIcon,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { changelogEntries } from '../data/changelog-entries';
import type { ChangelogEntry } from '../types';

type CategoryFilter = 'all' | ChangelogEntry['category'];

const categoryConfig: Record<
  ChangelogEntry['category'],
  { icon: typeof Sparkles; variant: 'default' | 'info' | 'success' }
> = {
  feature: { icon: Sparkles, variant: 'default' },
  improvement: { icon: Wrench, variant: 'info' },
  fix: { icon: Bug, variant: 'success' },
};

function formatDate(dateString: string, locale: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString(locale === 'fr' ? 'fr-CA' : 'en-CA', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

interface ChangelogEntryCardProps {
  entry: ChangelogEntry;
  locale: string;
}

function ChangelogEntryCard({ entry, locale }: ChangelogEntryCardProps) {
  const { t } = useTranslation('changelog');
  const [isOpen, setIsOpen] = useState(false);
  const config = categoryConfig[entry.category];
  const Icon = config.icon;

  const hasDetails = entry.details && entry.details.length > 0;
  const hasExpandableContent = hasDetails || entry.image;

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 mt-1">
            <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
              <Icon className="h-5 w-5 text-muted-foreground" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-2">
              <Badge variant={config.variant}>
                {t(`categories.${entry.category}`)}
              </Badge>
              {entry.legacyFrontend && (
                <Badge variant="outline" className="text-orange-600 border-orange-300">
                  {t('legacyFilter.badge')}
                </Badge>
              )}
              <span className="text-sm text-muted-foreground">
                {formatDate(entry.date, locale)}
              </span>
              {entry.image && (
                <ImageIcon className="h-4 w-4 text-muted-foreground" />
              )}
            </div>
            <h3 className="text-lg font-semibold mb-1">{entry.title}</h3>
            <p className="text-muted-foreground">{entry.description}</p>

            {hasExpandableContent && (
              <Collapsible open={isOpen} onOpenChange={setIsOpen}>
                <CollapsibleTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-2 -ml-2 text-muted-foreground hover:text-foreground"
                  >
                    {isOpen ? (
                      <ChevronDown className="h-4 w-4 mr-1" />
                    ) : (
                      <ChevronRight className="h-4 w-4 mr-1" />
                    )}
                    {t('showDetails')}
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  {hasDetails && (
                    <ul className="mt-3 space-y-1 pl-4">
                      {entry.details?.map((detail, index) => (
                        <li
                          key={index}
                          className="text-sm text-muted-foreground list-disc"
                        >
                          {detail}
                        </li>
                      ))}
                    </ul>
                  )}
                  {entry.image && (
                    <Dialog>
                      <DialogTrigger asChild>
                        <button
                          type="button"
                          className="mt-4 block cursor-zoom-in rounded-lg border border-border overflow-hidden hover:border-primary/50 transition-colors"
                        >
                          <img
                            src={`/changelog/${entry.image}`}
                            alt={entry.title}
                            className="max-w-full h-auto max-h-48 object-contain"
                          />
                        </button>
                      </DialogTrigger>
                      <DialogContent className="max-w-4xl">
                        <DialogTitle className="sr-only">
                          {entry.title}
                        </DialogTitle>
                        <img
                          src={`/changelog/${entry.image}`}
                          alt={entry.title}
                          className="w-full h-auto rounded-lg"
                        />
                      </DialogContent>
                    </Dialog>
                  )}
                </CollapsibleContent>
              </Collapsible>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function ChangelogPage() {
  const { t, i18n } = useTranslation('changelog');
  const [filter, setFilter] = useState<CategoryFilter>('all');
  const [showLegacy, setShowLegacy] = useState(false);

  const filteredEntries = changelogEntries.filter((entry) => {
    // Filter by category
    if (filter !== 'all' && entry.category !== filter) {
      return false;
    }
    // Filter out legacy frontend entries unless explicitly shown
    if (!showLegacy && entry.legacyFrontend) {
      return false;
    }
    return true;
  });

  const categories: CategoryFilter[] = ['all', 'feature', 'improvement', 'fix'];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">{t('title')}</h1>
        <p className="text-muted-foreground">{t('subtitle')}</p>
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <div className="flex flex-wrap gap-2">
          {categories.map((category) => (
            <Button
              key={category}
              variant={filter === category ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(category)}
            >
              {t(`filters.${category}`)}
            </Button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id="show-legacy"
            checked={showLegacy}
            onCheckedChange={(checked) => setShowLegacy(checked === true)}
          />
          <Label htmlFor="show-legacy" className="text-sm text-muted-foreground cursor-pointer">
            {t('legacyFilter.label')}
          </Label>
        </div>
      </div>

      <div className="space-y-4">
        {filteredEntries.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              {t('noEntries')}
            </CardContent>
          </Card>
        ) : (
          filteredEntries.map((entry) => (
            <ChangelogEntryCard
              key={entry.id}
              entry={entry}
              locale={i18n.language}
            />
          ))
        )}
      </div>
    </div>
  );
}
